import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../contexts/AuthContext";

const EMPTY = { sku: "", name: "", description: "", default_shelf_life_days: 30, units_label: "cases" };

export default function ProductsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null or {id, ...} or "new"
  const [form, setForm] = useState(EMPTY);

  const isAdmin = user?.role === "admin";

  const fetchAll = () => api.get("/products").then((r) => setRows(r.data));
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (row) => { setForm({ sku: row.sku, name: row.name, description: row.description, default_shelf_life_days: row.default_shelf_life_days, units_label: row.units_label }); setEditing(row.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/products", form);
      else await api.patch(`/products/${editing}`, form);
      toast.success("Saved");
      close();
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  return (
    <div data-testid="products-page">
      <PageHeader
        eyebrow="Catalog"
        title="Products."
        action={isAdmin && (
          <button onClick={openNew} className="mj-btn mj-btn-primary" data-testid="new-product-btn"><Plus size={14}/> New Product</button>
        )}
      />
      <div className="p-6 md:p-10 space-y-6">
        {editing && (
          <form onSubmit={submit} className="mj-card grid md:grid-cols-3 gap-4 items-end" data-testid="product-form">
            <div className="md:col-span-3 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">{editing === "new" ? "New product" : "Edit product"}</h3>
              <button type="button" onClick={close} className="mj-btn mj-btn-ghost" data-testid="product-form-cancel"><X size={14}/> Cancel</button>
            </div>
            <div><label className="data-label block mb-2">SKU</label><input required className="mj-input font-mono-id" value={form.sku} onChange={(e)=>setForm({...form, sku:e.target.value})} data-testid="product-sku-input"/></div>
            <div><label className="data-label block mb-2">Name</label><input required className="mj-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="product-name-input"/></div>
            <div><label className="data-label block mb-2">Units label</label><input className="mj-input" value={form.units_label} onChange={(e)=>setForm({...form, units_label:e.target.value})} data-testid="product-units-input"/></div>
            <div className="md:col-span-2"><label className="data-label block mb-2">Description</label><input className="mj-input" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="product-desc-input"/></div>
            <div><label className="data-label block mb-2">Shelf life (days)</label><input type="number" min={1} required className="mj-input font-mono-id" value={form.default_shelf_life_days} onChange={(e)=>setForm({...form, default_shelf_life_days:parseInt(e.target.value, 10)})} data-testid="product-shelf-input"/></div>
            <div className="md:col-span-3"><button type="submit" className="mj-btn mj-btn-primary" data-testid="product-submit-btn">Save</button></div>
          </form>
        )}

        <div className="mj-card">
          <table className="mj-table" data-testid="products-table">
            <thead><tr><th>SKU</th><th>Name</th><th>Description</th><th>Units</th><th>Shelf life</th>{isAdmin && <th></th>}</tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500">No products yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono-id font-bold">{r.sku}</td>
                  <td>{r.name}</td>
                  <td className="text-[var(--mj-ink-muted)]">{r.description}</td>
                  <td className="font-mono-id">{r.units_label}</td>
                  <td className="font-mono-id">{r.default_shelf_life_days} d</td>
                  {isAdmin && (
                    <td className="text-right">
                      <button onClick={() => openEdit(r)} className="mj-btn mj-btn-ghost" style={{minHeight: 36, padding: "0 10px"}} data-testid={`edit-product-${r.id}`}><Pencil size={14}/></button>
                      <button onClick={() => remove(r.id)} className="mj-btn mj-btn-ghost" style={{minHeight: 36, padding: "0 10px", color: "var(--mj-red)"}} data-testid={`del-product-${r.id}`}><Trash2 size={14}/></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
